import { cache, Enums, eventTarget, type Types } from '@cornerstonejs/core';
import type vtkImageData from '@kitware/vtk.js/Common/DataModel/ImageData';
import type {
  LabelmapToolOperationDataStack,
  LabelmapToolOperationDataVolume,
} from '../../../../types';
import { getCurrentLabelmapImageIdForViewport } from '../../../../stateManagement/segmentation/segmentationState';
import { getLabelmapActorEntry } from '../../../../stateManagement/segmentation/helpers';
import { getReferenceVolumeForSegmentationVolume } from '../../../../utilities/segmentation/getReferenceVolumeForSegmentationVolume';

/**
 * Data that brush / labelmap strategies need from the cache for one edit.
 *
 * Explicit so `declaration` emit does not try to name VTK `ImageData` through
 * `@cornerstonejs/core/node_modules/...` (TS2742).
 */
export type StrategyData = {
  segmentationImageData: vtkImageData;
  /**
   * Volume voxel managers still expose the recorded-write `boundsIJK` field
   * that {@link getStatistics} reads; keep it on the type so declaration emit
   * does not collapse to a bare {@link Types.IVoxelManager}.
   */
  segmentationVoxelManager: Types.IVoxelManager<number> & {
    boundsIJK?: Types.BoundsIJK;
  };
  segmentationScalarData: unknown;
  imageScalarData: unknown;
  imageVoxelManager:
    | Types.IVoxelManager<number>
    | Types.IVoxelManager<Types.RGB>
    | undefined;
  imageData: vtkImageData | null | undefined;
};

/**
 * Get strategy data for volume viewport
 * @param operationData - The operation data containing volumeId and referencedVolumeId
 * @returns The strategy data for volume viewport or null if error
 */
function getStrategyDataForVolumeViewport({
  operationData,
}): StrategyData | null {
  const { volumeId } = operationData;

  if (!volumeId) {
    const event = new CustomEvent(Enums.Events.ERROR_EVENT, {
      detail: {
        type: 'Segmentation',
        message: 'No volume id found for the segmentation',
      },
      cancelable: true,
    });
    eventTarget.dispatchEvent(event);
    return null;
  }

  const segmentationVolume = cache.getVolume(volumeId);
  const imageVolume = getReferenceVolumeForSegmentationVolume(volumeId);

  if (!segmentationVolume || !imageVolume) {
    return null;
  }

  const { imageData: segmentationImageData } = segmentationVolume;
  const { voxelManager: segmentationVoxelManager } = segmentationVolume;
  const { voxelManager: imageVoxelManager, imageData } = imageVolume;

  return {
    segmentationImageData,
    segmentationVoxelManager,
    segmentationScalarData: null,
    imageScalarData: null,
    imageVoxelManager,
    imageData,
  } as StrategyData;
}

/**
 * Get strategy data for stack viewport
 * @param operationData - The operation data containing segmentationId and imageId
 * @param viewport - The viewport instance
 * @returns The strategy data for stack viewport or null if error
 */
function getStrategyDataForStackViewport({
  operationData,
  viewport,
  strategy,
}): StrategyData | null {
  const { segmentationId } = operationData as LabelmapToolOperationDataStack;

  let segmentationImageData;
  let segmentationVoxelManager;
  let getSegmentationScalarData: () => unknown;
  let imageScalarData;
  let imageVoxelManager;
  let imageData;
  if (strategy.ensureSegmentationVolumeFor3DManipulation) {
    // Todo: I don't know how to handle this, seems like strategies cannot return anything
    // and just manipulate the operationData?
    strategy.ensureSegmentationVolumeFor3DManipulation({
      operationData,
      viewport,
    });

    segmentationVoxelManager = operationData.segmentationVoxelManager;
    segmentationImageData = operationData.segmentationImageData;
    getSegmentationScalarData = () => null;
  } else {
    const labelmapImageId = getCurrentLabelmapImageIdForViewport(
      viewport.id,
      segmentationId
    );
    if (!labelmapImageId) {
      return null;
    }
    const actorEntry = getLabelmapActorEntry(
      viewport.id,
      segmentationId,
      labelmapImageId
    );

    if (!actorEntry) {
      return null;
    }
    const currentSegImage = cache.getImage(labelmapImageId);
    segmentationImageData = actorEntry.actor.getMapper().getInputData();
    segmentationVoxelManager = currentSegImage.voxelManager;

    const currentSegmentationImageId = operationData.imageId;

    const segmentationImage = cache.getImage(currentSegmentationImageId);
    if (!segmentationImage) {
      return null;
    }
    // A thunk, not a call: on an RLE backed labelmap image `getPixelData()`
    // expands the whole frame, and `getStrategyData` runs on every pointer move
    // of a brush. No caller reads `segmentationScalarData` today, so the eager
    // call bought a full frame decode and a frame sized allocation per event.
    // The thunk keeps the field available for a caller that does want it.
    getSegmentationScalarData = () => segmentationImage.getPixelData?.();
  }

  if (strategy.ensureImageVolumeFor3DManipulation) {
    strategy.ensureImageVolumeFor3DManipulation({
      operationData,
      viewport,
    });

    imageVoxelManager = operationData.imageVoxelManager;
    imageScalarData = operationData.imageScalarData;
    imageData = operationData.imageData;
  } else {
    const currentImageId = viewport.getCurrentImageId();
    if (!currentImageId) {
      return null;
    }

    const image = cache.getImage(currentImageId);
    imageData = image ? null : viewport.getImageData();

    // VERY IMPORTANT
    // This is the pixel data of the image that is being segmented in the cache
    // and we need to use this to for the modification
    imageScalarData = image?.getPixelData() || imageData.getScalarData();
    imageVoxelManager = image?.voxelManager;
  }

  return {
    segmentationImageData,
    get segmentationScalarData() {
      return getSegmentationScalarData();
    },
    imageScalarData,
    segmentationVoxelManager,
    imageVoxelManager,
    imageData,
  } as StrategyData;
}

/**
 * Get strategy data based on viewport type
 * @param params - Object containing operationData and viewport
 * @returns The strategy data or null if error
 */
function getStrategyData({
  operationData,
  viewport,
  strategy,
}: {
  operationData:
    | LabelmapToolOperationDataStack
    | LabelmapToolOperationDataVolume;
  viewport?: Types.IStackViewport | Types.IVolumeViewport;
  strategy: unknown;
}): StrategyData | null {
  if (!operationData) {
    return null;
  }

  if (
    ('volumeId' in operationData && operationData.volumeId != null) ||
    ('referencedVolumeId' in operationData &&
      operationData.referencedVolumeId != null)
  ) {
    return getStrategyDataForVolumeViewport({ operationData });
  }

  return getStrategyDataForStackViewport({ operationData, viewport, strategy });
}

export { getStrategyData };
